import { ArrowUp, Instagram } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import Logo from "./Logo";
import Wordmark from "./Wordmark";

export default function Footer() {
  const { t } = useLang();
  const navigate = useNavigate();
  const links = [
    { id: "/chi-siamo", label: t.nav.about },
    { id: "/metodo", label: t.nav.metodo },
    { id: "/showroom", label: t.nav.showroom },
    { id: "/works", label: t.nav.portfolio },
    { id: "/contatti", label: t.collab.overline },
  ];

  return (
    <footer data-testid="site-footer" className="border-t border-line/70 pt-20 pb-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-5">
            <button
              onClick={() => { navigate("/"); window.scrollTo(0, 0); }}
              data-testid="footer-logo-button"
              aria-label="Green Gate — home"
              className="flex items-center gap-3 mb-6 group cursor-pointer"
            >
              <Logo className="w-9 h-9" />
              <Wordmark className="h-7 w-auto text-cream group-hover:text-brass transition-colors duration-300" />
            </button>
            <p className="text-stone font-light leading-relaxed max-w-xs">{t.footer.tagline}</p>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow text-[10px] text-stone/70 mb-6">{t.footer.navTitle}</p>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id.replace("/", "")}`}
                    onClick={() => { navigate(l.id); window.scrollTo(0, 0); }}
                    className="text-cream/80 font-light hover:text-brass transition-colors duration-300"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="eyebrow text-[10px] text-stone/70 mb-6">{t.footer.contactTitle}</p>
            <p className="text-cream/80 font-light whitespace-pre-line leading-relaxed">
              {t.contact.info.showroom}
            </p>
            <a
              href={`mailto:${t.contact.info.email}`}
              data-testid="footer-email-link"
              className="block mt-4 text-cream/80 font-light hover:text-brass transition-colors duration-300"
            >
              {t.contact.info.email}
            </a>
            <a
              href={`tel:${t.contact.info.phone.replace(/\s/g, "")}`}
              data-testid="footer-phone-link"
              className="block mt-1 text-cream/80 font-light hover:text-brass transition-colors duration-300"
            >
              {t.contact.info.phone}
            </a>
            <a
              href="https://www.instagram.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="Instagram"
              className="mt-6 inline-flex items-center gap-3 text-cream/80 hover:text-brass transition-colors duration-300 group"
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-line group-hover:border-brass group-hover:-rotate-6 group-hover:scale-110 transition-[border-color,transform] duration-400">
                <Instagram size={18} strokeWidth={1.5} />
              </span>
              <span className="text-sm font-light">Instagram</span>
            </a>
          </div>
        </div>

        <div className="select-none" aria-hidden="true">
          <Wordmark label="green gate" className="text-cream w-full h-auto md:max-w-[1150px] md:mx-auto" />
        </div>

        <p
          data-testid="footer-slogan"
          className="text-center mt-4 font-serif italic text-lg md:text-2xl text-cream/90 tracking-wide"
        >
          {t.footer.slogan}
        </p>

        <p
          data-testid="footer-disclaimer"
          className="mt-8 text-[12px] text-stone/70 font-light leading-relaxed max-w-3xl"
        >
          {t.footer.disclaimer}
        </p>

        <div className="mt-10 pt-8 border-t border-line/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-[13px] text-stone">
          <p data-testid="footer-copyright">{t.footer.rights}</p>
          <p>{t.footer.piva}</p>
          <a
            href="/admin"
            data-testid="footer-admin-link"
            className="text-[12px] text-stone/60 hover:text-brass transition-colors duration-300"
          >
            {t.footer.admin}
          </a>
          <button
            data-testid="footer-back-to-top"
            onClick={() => (window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.6 }) : window.scrollTo({ top: 0, behavior: "smooth" }))}
            className="inline-flex items-center gap-2 uppercase tracking-[0.16em] text-brass hover:text-cream transition-colors duration-300"
          >
            {t.footer.top}
            <ArrowUp size={15} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </footer>
  );
}
